export const NOTIFICATION_DISPLAY_TYPES = ['Modal', 'Toast'] as const
export type NotificationDisplayType =
  (typeof NOTIFICATION_DISPLAY_TYPES)[number]

export const NOTIFICATION_LOGIN_STATES = [
  'Everyone',
  'Logged in only',
  'Logged out only',
] as const
export type NotificationLoginState = (typeof NOTIFICATION_LOGIN_STATES)[number]

export const NOTIFICATION_ROLE_NAMES = [
  'STORE_OWNER',
  'STORE_ADMIN',
  'CORPORATE_ADMIN',
  'CORPORATE_OWNER',
  'SCOREKEEPER',
] as const
export type NotificationRoleName = (typeof NOTIFICATION_ROLE_NAMES)[number]

export const NOTIFICATION_SELECTOR_MODES = ['inclusion', 'exclusion'] as const
export type NotificationSelectorMode =
  (typeof NOTIFICATION_SELECTOR_MODES)[number]

export const NOTIFICATION_ADDITIONAL_CONDITION_TYPES = [
  'termsAcceptanceRequired',
] as const
export type NotificationAdditionalConditionType =
  (typeof NOTIFICATION_ADDITIONAL_CONDITION_TYPES)[number]

export interface NotificationAdditionalCondition {
  type: NotificationAdditionalConditionType
  params: {
    /** ISO-8601 instant; new Contentful app values use a fixed -08:00 offset. */
    effectiveAt: string
  }
}

export const NOTIFICATION_TERMS_MOCK_STATUSES = [
  'unsigned',
  'stale',
  'current',
] as const
export type NotificationTermsMockStatus =
  (typeof NOTIFICATION_TERMS_MOCK_STATUSES)[number]

export type NotificationSource = 'global' | 'page'

export interface NotificationAudienceRule {
  selector: NotificationSelectorMode
  values: string[]
}

export interface SiteNotificationCta {
  id?: string
  fields: Record<string, unknown>
  [key: string]: unknown
}

/** A resolved registry reference held until normalization. */
export interface SiteNotificationRegistryReference {
  id?: string
  sys?: unknown
  fields?: unknown
}

export interface SiteNotificationAudience {
  language?: NotificationAudienceRule
  storeCountry?: NotificationAudienceRule
  storeId?: NotificationAudienceRule
  role?: NotificationAudienceRule
}

export interface SiteNotification {
  id: string
  entryTitle: string
  heading: string
  body: string
  ctas: SiteNotificationCta[]
  dismissLabel: string
  displayType: NotificationDisplayType
  blocking: boolean
  loginState: NotificationLoginState
  additionalCondition?: NotificationAdditionalCondition
  audience: SiteNotificationAudience
  source: NotificationSource
  sourceOrder: number
  matchedStoreIds?: string[]
}

export interface NotificationOrganizationRoleInput {
  roleName?: string | null
  [key: string]: unknown
}

export interface NotificationOrganizationInput {
  id?: string | null
  country?: string | null
  /** ISO timestamp; null means the organization has never accepted Terms. */
  acceptedTermsAndConditionsAt?: string | null
  roleName?: string | null
  roles?: Array<string | NotificationOrganizationRoleInput | null> | null
  roleNames?: Array<string | null> | null
  [key: string]: unknown
}

/** Compatibility alias for userStore.stores, whose values are organizations. */
export type NotificationStoreInput = NotificationOrganizationInput

export interface NotificationUserRoleInput {
  roleName?: string | null
  organization?: NotificationOrganizationInput | null
  [key: string]: unknown
}

export interface NotificationAudienceContext {
  locale?: string | null
  visitorCountry?: string | null
  stores?: Array<NotificationStoreInput | null | undefined> | null
  /** Role records are used to associate roles with the same organization ID. */
  roles?: Array<NotificationUserRoleInput | null | undefined> | null
  /** Canonical Tabletop name for records exposed as stores by the user store. */
  organizations?: Array<NotificationOrganizationInput | null | undefined> | null
  authenticated?: boolean
  ready?: boolean
}

/** Development-only evaluator context supplied by query-string previews. */
export interface SiteNotificationConditionMock {
  locale: string
  country: string
  storeIds: string[]
  roles: string[]
  termsStatus?: NotificationTermsMockStatus
}

export interface NotificationDiagnostic {
  level: 'warning' | 'error'
  code: string
  entryId: string
  dimension?: string
  rawValue?: unknown
  message: string
}

export interface NotificationLogger {
  warn?: (message: string, details?: NotificationDiagnostic) => void
  error?: (message: string, details?: NotificationDiagnostic) => void
}

export interface NotificationEvaluationOptions {
  context: NotificationAudienceContext
  globalEntries?: readonly unknown[] | null
  pageEntries?: readonly unknown[] | null
  routeSlug?: string | string[] | null
  logger?: NotificationLogger
}

export interface NotificationEvaluationResult {
  candidates: SiteNotification[]
  warnings: NotificationDiagnostic[]
  errors: NotificationDiagnostic[]
}

export interface NotificationEligibilityResult {
  eligible: boolean
  matchedStoreIds: string[]
}
